import { formatCodeableConcept } from '@medplum/core';
import type { Age, CodeableConcept, FamilyMemberHistory, Period, Quantity, Range } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import type { TFunction } from 'i18next';
import { DATE_FORMATS } from '../../utils/date';

// Common HL7 v3-RoleCode values used in FamilyMemberHistory.relationship.
// Used only as a last-resort fallback when neither .text nor .coding[0].display
// is set on the resource. Codes outside this map fall through to the raw code,
// so nothing renders blank.
const RELATIONSHIP_CODE_LABELS: Record<string, string> = {
  FTH: 'Father',
  MTH: 'Mother',
  PRN: 'Parent',
  SON: 'Son',
  DAU: 'Daughter',
  CHILD: 'Child',
  NSIB: 'Sibling',
  SIB: 'Sibling',
  NBRO: 'Brother',
  BRO: 'Brother',
  NSIS: 'Sister',
  SIS: 'Sister',
  HBRO: 'Half-brother',
  HSIS: 'Half-sister',
  TWIN: 'Twin',
  PGRFTH: 'Paternal grandfather',
  PGRMTH: 'Paternal grandmother',
  MGRFTH: 'Maternal grandfather',
  MGRMTH: 'Maternal grandmother',
  GRFTH: 'Grandfather',
  GRMTH: 'Grandmother',
  GRPRN: 'Grandparent',
  PUNCLE: 'Paternal uncle',
  MUNCLE: 'Maternal uncle',
  UNCLE: 'Uncle',
  PAUNT: 'Paternal aunt',
  MAUNT: 'Maternal aunt',
  AUNT: 'Aunt',
  COUSN: 'Cousin',
  NEPHEW: 'Nephew',
  NIECE: 'Niece',
  NMTH: 'Birth mother',
  NFTH: 'Birth father',
};

export const formatRelationship = (relationship: CodeableConcept | undefined, t: TFunction<'records'>): string => {
  if (!relationship) return t('unknownRelationship');
  if (relationship.text) return relationship.text;
  const coding = relationship.coding?.find((c) => c.display || c.code);
  if (coding?.display) return coding.display;
  if (coding?.code) return RELATIONSHIP_CODE_LABELS[coding.code] ?? coding.code;
  return t('unknownRelationship');
};

const isAgeOrQuantity = (value: unknown): value is Quantity =>
  typeof value === 'object' && value !== null && 'value' in value && typeof (value as Quantity).value === 'number';

const isRange = (value: unknown): value is Range =>
  typeof value === 'object' && value !== null && ('low' in value || 'high' in value);

const formatAgeUnit = (q: Age | Quantity, t: TFunction<'records'>): string => {
  if (q.value === undefined) return '';
  switch (q.code ?? q.unit) {
    case 'a':
    case 'years':
    case 'year':
      return t('ageYears', { count: q.value });
    case 'mo':
    case 'months':
    case 'month':
      return t('ageMonths', { count: q.value });
    case 'wk':
    case 'weeks':
    case 'week':
      return t('ageWeeks', { count: q.value });
    case 'd':
    case 'days':
    case 'day':
      return t('ageDays', { count: q.value });
    default:
      return q.unit ? `${q.value} ${q.unit}` : String(q.value);
  }
};

type AgeLike = Age | Range | string | undefined;

export const formatAge = (age: AgeLike, t: TFunction<'records'>): string | undefined => {
  if (age === undefined) return undefined;
  if (typeof age === 'string') return age;
  if (isRange(age)) {
    const low = age.low ? formatAgeUnit(age.low, t) : undefined;
    const high = age.high ? formatAgeUnit(age.high, t) : undefined;
    if (low && high) return `${low}–${high}`;
    return low ?? high;
  }
  if (isAgeOrQuantity(age)) return formatAgeUnit(age, t);
  return undefined;
};

const formatPeriod = (period: Period): string | undefined => {
  const start = period.start ? format(parseISO(period.start), DATE_FORMATS.ISO_DATE) : undefined;
  const end = period.end ? format(parseISO(period.end), DATE_FORMATS.ISO_DATE) : undefined;
  if (start && end) return `${start} – ${end}`;
  return start ?? end;
};

export const formatBorn = (history: FamilyMemberHistory): string | undefined => {
  if (history.bornDate) return format(parseISO(history.bornDate), DATE_FORMATS.ISO_DATE);
  if (history.bornString) return history.bornString;
  if (history.bornPeriod) return formatPeriod(history.bornPeriod);
  return undefined;
};

export const formatDeceased = (history: FamilyMemberHistory, t: TFunction<'records'>): string | undefined => {
  if (history.deceasedBoolean === true) return t('deceased');
  if (history.deceasedBoolean === false) return undefined;
  if (history.deceasedDate)
    return t('deceasedOn', { date: format(parseISO(history.deceasedDate), DATE_FORMATS.ISO_DATE) });
  if (history.deceasedString) return t('deceasedWithDetail', { detail: history.deceasedString });
  if (history.deceasedAge) {
    const age = formatAgeUnit(history.deceasedAge, t);
    return age ? t('deceasedAtAge', { age }) : t('deceased');
  }
  if (history.deceasedRange) {
    const age = formatAge(history.deceasedRange, t);
    return age ? t('deceasedAtAge', { age }) : t('deceased');
  }
  return undefined;
};

export const summarizeConditions = (history: FamilyMemberHistory, t: TFunction<'records'>): string | undefined => {
  if (!history.condition || history.condition.length === 0) return undefined;
  const max = 3;
  const labels = history.condition.slice(0, max).map((c) => formatCodeableConcept(c.code) || t('unknownCondition'));
  const remaining = history.condition.length - max;
  if (remaining > 0) labels.push(t('andMore', { count: remaining }));
  return labels.join(', ');
};
